/**
 * Sign in, passkeys, step-up, sessions, email change and `/me` (docs/53 §4.1, §4.2, §6.1).
 * WebAuthn shapes follow the W3C JSON serialisation (PublicKeyCredential*JSON), which is what
 * `navigator.credentials` returns through `toJSON()` and what `@simplewebauthn` accepts.
 */
import { z } from 'zod';
import { AppPath, Email, IsoDateTime, JsonValue, text } from '../common.ts';
import { AuthMethod, ClientRole, TenantStatus } from '../enums.ts';
import { LocationId, SessionRef, TenantId, UserId } from '../ids.ts';

const B64Url = z.string().regex(/^[A-Za-z0-9_-]+$/).max(16_384).meta({ examples: ['dGhpcy1pcy1hLWNoYWxsZW5nZQ'] });

// ---------------------------------------------------------------------------------------------- email code

export const EmailStartBody = z
  .object({
    email: Email,
    /** Invisible Turnstile token on app sign-in (docs/52 S01). */
    turnstileToken: z.string().max(4096).optional(),
    /** Where to land after sign-in. Relative only. */
    next: AppPath.optional(),
  })
  .strict();

/** Identical whether or not the email is known (docs/53 §4.1). */
export const EmailStartResponse = z.object({
  ok: z.literal(true),
  maskedEmail: z.string().max(260).meta({ examples: ['d•••@riveraplumbing.com'] }),
  expiresInSeconds: z.literal(900),
  resendAfterSeconds: z.number().int().min(0).max(300),
});

export const EmailVerifyBody = z
  .object({
    code: z.string().regex(/^\d{6}$/, 'expected six digits'),
    /**
     * The link token, sent only from the link page on a browser that didn't ask for the code (the `need_code`
     * answer of LinkConfirmResponse). Without it the code must come from the browser that asked (docs/53 §4.1).
     */
    token: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/).optional(),
  })
  .strict();

export const SignedIn = z.object({
  userId: UserId,
  next: AppPath,
  /** Offer a passkey on this device (docs/52 S03). */
  offerPasskey: z.boolean(),
  newDevice: z.boolean(),
});

export const LinkConfirmBody = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) }).strict();

/** A link opened on another browser has no nonce, so it asks for the code instead (docs/53 §4.1 Link). */
export const LinkConfirmResponse = z.discriminatedUnion('result', [
  z.object({ result: z.literal('signed_in'), signedIn: SignedIn }),
  z.object({ result: z.literal('need_code'), maskedEmail: z.string().max(260) }),
]);

// ---------------------------------------------------------------------------------------------- passkeys

const AuthenticatorTransport = z.enum(['ble', 'cable', 'hybrid', 'internal', 'nfc', 'smart-card', 'usb']);
const CredentialDescriptor = z.object({
  id: B64Url,
  type: z.literal('public-key'),
  transports: z.array(AuthenticatorTransport).optional(),
});

export const PasskeyRegisterOptions = z.object({
  challenge: B64Url,
  rp: z.object({ id: z.string().max(253), name: z.string().max(64) }),
  user: z.object({ id: B64Url, name: z.string().max(254), displayName: z.string().max(120) }),
  pubKeyCredParams: z.array(z.object({ type: z.literal('public-key'), alg: z.number().int() })).min(1),
  timeout: z.number().int().positive().optional(),
  excludeCredentials: z.array(CredentialDescriptor).optional(),
  authenticatorSelection: z
    .object({
      residentKey: z.enum(['discouraged', 'preferred', 'required']).optional(),
      userVerification: z.literal('required'),
      authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
    })
    .optional(),
  attestation: z.enum(['none', 'indirect', 'direct', 'enterprise']).optional(),
});

export const PasskeyRegisterVerifyBody = z
  .object({
    label: text(60).optional(),
    credential: z
      .object({
        id: B64Url,
        rawId: B64Url,
        type: z.literal('public-key'),
        response: z.object({
          clientDataJSON: B64Url,
          attestationObject: B64Url,
          transports: z.array(AuthenticatorTransport).optional(),
          publicKeyAlgorithm: z.number().int().optional(),
          publicKey: B64Url.optional(),
          authenticatorData: B64Url.optional(),
        }),
        authenticatorAttachment: z.enum(['platform', 'cross-platform']).nullable().optional(),
        clientExtensionResults: z.record(z.string(), JsonValue),
      })
      .strict(),
  })
  .strict();

export const PasskeySummary = z.object({
  id: B64Url,
  label: z.string().max(60).nullable(),
  createdAt: IsoDateTime,
  lastUsedAt: IsoDateTime.nullable(),
  backedUp: z.boolean().nullable(),
});

export const PasskeyLoginOptionsBody = z.object({ email: Email.optional() }).strict();

export const PasskeyLoginOptions = z.object({
  challenge: B64Url,
  rpId: z.string().max(253),
  timeout: z.number().int().positive().optional(),
  allowCredentials: z.array(CredentialDescriptor).optional(),
  userVerification: z.literal('required'),
});

export const PasskeyAssertion = z
  .object({
    id: B64Url,
    rawId: B64Url,
    type: z.literal('public-key'),
    response: z.object({
      clientDataJSON: B64Url,
      authenticatorData: B64Url,
      signature: B64Url,
      userHandle: B64Url.nullable().optional(),
    }),
    authenticatorAttachment: z.enum(['platform', 'cross-platform']).nullable().optional(),
    clientExtensionResults: z.record(z.string(), JsonValue),
  })
  .strict();

export const PasskeyLoginVerifyBody = z.object({ credential: PasskeyAssertion, next: AppPath.optional() }).strict();

// ---------------------------------------------------------------------------------------------- step-up

export const StepUpBody = z.discriminatedUnion('method', [
  z.object({ method: z.literal('passkey'), credential: PasskeyAssertion }).strict(),
  /** Sends a fresh code to the signed-in user's email. */
  z.object({ method: z.literal('email_code_start') }).strict(),
  z.object({ method: z.literal('email_code'), code: z.string().regex(/^\d{6}$/) }).strict(),
]);

export const StepUpOptions = z.object({ passkey: PasskeyLoginOptions.nullable(), emailAvailable: z.boolean() });

export const StepUpResponse = z.discriminatedUnion('result', [
  z.object({ result: z.literal('stepped_up'), stepUpUntil: IsoDateTime }),
  z.object({ result: z.literal('code_sent'), maskedEmail: z.string().max(260) }),
]);

// ---------------------------------------------------------------------------------------------- sessions

export const SessionSummary = z.object({
  ref: SessionRef,
  deviceLabel: z.string().max(80).nullable(),
  uaSummary: z.string().max(120).nullable(),
  lastSeenAt: IsoDateTime,
  lastCity: z.string().max(80).nullable(),
  authMethod: AuthMethod,
  current: z.boolean(),
  createdAt: IsoDateTime,
});
export const SessionList = z.object({ items: z.array(SessionSummary) });

/** "This wasn't me" from the new-device email revokes every session (docs/53 §4.1). */
export const RevokeAllBody = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) }).strict();

// ---------------------------------------------------------------------------------------------- email change

export const EmailChangeBody = z.object({ newEmail: Email }).strict();
export const EmailChangeResponse = z.object({ effectiveAt: IsoDateTime, maskedNewEmail: z.string().max(260) });
export const EmailChangeTokenBody = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) }).strict();

// ---------------------------------------------------------------------------------------------- me

export const MembershipSummary = z.object({
  tenantId: TenantId,
  tenantName: z.string().max(60),
  tenantStatus: TenantStatus,
  role: ClientRole,
  canApprove: z.boolean(),
  /** null means every location. */
  locationIds: z.array(LocationId).nullable(),
  locations: z.array(z.object({ id: LocationId, name: z.string().max(80) })),
  siteEnabled: z.boolean(),
});

export const Me = z.object({
  user: z.object({
    id: UserId,
    email: Email,
    name: z.string().max(80).nullable(),
    locale: z.string().max(16),
    emailVerifiedAt: IsoDateTime.nullable(),
    hasPasskey: z.boolean(),
    pendingEmailChange: z.object({ maskedNewEmail: z.string().max(260), effectiveAt: IsoDateTime }).nullable(),
  }),
  memberships: z.array(MembershipSummary),
  session: z.object({ ref: SessionRef, authMethod: AuthMethod, stepUpUntil: IsoDateTime.nullable(), idleExpiresAt: IsoDateTime }),
  /** Feature flags resolved for this user (global plus tenant rows). */
  flags: z.record(z.string(), JsonValue),
});

export const UpdateMeBody = z.object({ name: text(80).optional(), locale: z.enum(['en-US', 'es-US']).optional() }).strict();
